import React, { useState, useMemo, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Box,
  RadioGroup,
  Radio,
  FormControlLabel,
  Chip,
  Divider,
  Checkbox,
} from '@mui/material'
import { TestService } from '../../services/testService'
import { TestGenerationRequest, LONG_CONTENT_THRESHOLD } from '../../types/test'
import { ComplianceRule } from '../../types/compliance'
import { apiService } from '../../services/api'
import { complianceService } from '../../services/complianceService'
import { useAuth } from '../../contexts/AuthContext'

// クレジット計算定数（バックエンドと同じ）
const CHARS_PER_CREDIT = 1500

interface TestGenerationModalProps {
  open: boolean
  onClose: () => void
  rule?: ComplianceRule // オプショナル: 未指定時はモーダル内で選択
  onSuccess: () => void
}

export const TestGenerationModal: React.FC<TestGenerationModalProps> = ({
  open,
  onClose,
  rule,
  onSuccess,
}) => {
  const { user } = useAuth()
  const [questionCount, setQuestionCount] = useState<number>(5)
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium')
  const [splitOption, setSplitOption] = useState<'multiple' | 'combined' | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 学習教材選択関連（rule未指定時に使用）
  const [complianceRules, setComplianceRules] = useState<ComplianceRule[]>([])
  const [selectedRule, setSelectedRule] = useState<ComplianceRule | null>(rule || null)
  const [rulesLoading, setRulesLoading] = useState(false)

  // クレジット関連
  const [creditBalance, setCreditBalance] = useState<number | null>(null)
  const [creditLoading, setCreditLoading] = useState(false)
  const [isUnlimitedCredits, setIsUnlimitedCredits] = useState(false)
  const [aiCreditPrice, setAiCreditPrice] = useState<number>(0)

  // チャージ関連
  const [charging, setCharging] = useState(false)
  const [agreeToCharge, setAgreeToCharge] = useState(false)

  // 長いコンテンツかどうかを判定
  const isLongContent = useMemo(() => {
    return selectedRule?.content ? selectedRule.content.length >= LONG_CONTENT_THRESHOLD : false
  }, [selectedRule?.content])

  // ページ数を計算（1,500文字 = 1ページ）
  const pageCount = useMemo(() => {
    return selectedRule?.content ? Math.ceil(selectedRule.content.length / 1500) : 0
  }, [selectedRule?.content])

  // 必要なクレジット数を計算
  const creditsRequired = useMemo(() => {
    if (!selectedRule?.content) return 0
    return Math.ceil(selectedRule.content.length / CHARS_PER_CREDIT)
  }, [selectedRule?.content])

  // 不足クレジット数
  const creditsShortage = useMemo(() => {
    if (isUnlimitedCredits || creditBalance === null) return 0
    return Math.max(0, creditsRequired - creditBalance)
  }, [creditsRequired, creditBalance, isUnlimitedCredits])

  // チャージ費用
  const chargeCost = useMemo(() => {
    return creditsShortage * aiCreditPrice
  }, [creditsShortage, aiCreditPrice])

  // クレジット不足かどうか（無制限の場合は常にfalse）
  const isInsufficientCredit =
    !isUnlimitedCredits && creditBalance !== null && creditBalance < creditsRequired

  // チャージ可能かどうか（追加クレジット単価が設定されている場合のみ）
  const canCharge = aiCreditPrice > 0

  // 最大問題数（分割時は多めに設定可能）
  const maxQuestions = isLongContent && splitOption ? 50 : 20

  // propsのruleが変わったらselectedRuleを同期
  useEffect(() => {
    if (rule) {
      setSelectedRule(rule)
    }
  }, [rule])

  // モーダルが開いたときに学習教材一覧を取得（rule未指定時のみ）
  useEffect(() => {
    const loadComplianceRules = async () => {
      if (rule) return // ruleが指定されている場合はスキップ
      try {
        setRulesLoading(true)
        const response = await complianceService.getRules({ limit: 100, isActive: true })
        setComplianceRules(response.rules)
      } catch (err) {
        console.error('学習教材一覧取得エラー:', err)
        setError('学習教材一覧の取得に失敗しました')
      } finally {
        setRulesLoading(false)
      }
    }
    if (open && !rule) {
      loadComplianceRules()
    }
  }, [open, rule])

  // 学習教材を選択したときに詳細を取得（contentフィールドが必要）
  const handleRuleSelect = async (ruleId: string) => {
    try {
      setError('')
      const fullRule = await complianceService.getRule(ruleId)
      setSelectedRule(fullRule)
    } catch (err) {
      console.error('学習教材詳細取得エラー:', err)
      setError('学習教材の詳細取得に失敗しました')
    }
  }

  // モーダルが開いたときにクレジット残高を取得
  useEffect(() => {
    if (open && selectedRule) {
      fetchCreditBalance()
      setAgreeToCharge(false)
    }
  }, [open, selectedRule])

  const fetchCreditBalance = async () => {
    try {
      setCreditLoading(true)
      const response = await apiService.get<{
        success: boolean
        data: {
          creditsRemaining: number | null
          isUnlimited?: boolean
          plan?: {
            aiCreditPrice?: number
          }
        }
      }>('/billing/credits/balance')
      if (response.success && response.data) {
        if (response.data.isUnlimited) {
          setIsUnlimitedCredits(true)
          setCreditBalance(null)
        } else {
          setIsUnlimitedCredits(false)
          setCreditBalance(response.data.creditsRemaining)
        }
        // 追加クレジット単価を設定
        setAiCreditPrice(response.data.plan?.aiCreditPrice || 0)
      }
    } catch (err) {
      console.error('クレジット残高取得エラー:', err)
      // エラーでもnullのまま（チェックをスキップ）
    } finally {
      setCreditLoading(false)
    }
  }

  // クレジットをチャージして生成
  const handleChargeAndGenerate = async () => {
    try {
      setCharging(true)
      setError('')

      // クレジット購入API呼び出し
      const purchaseResponse = await apiService.post<{
        success: boolean
        data?: {
          creditsAdded: number
          newBalance: number
          totalCost: number
        }
        error?: string
      }>('/billing/credits/purchase', {
        credits: creditsShortage,
        reason: `AIテスト生成用クレジット購入（学習教材: ${selectedRule?.title}）`,
      })

      if (!purchaseResponse.success) {
        throw new Error(purchaseResponse.error || 'クレジット購入に失敗しました')
      }

      // 残高を更新
      if (purchaseResponse.data) {
        setCreditBalance(purchaseResponse.data.newBalance)
      }

      // テスト生成を実行
      await handleGenerate()
    } catch (err: any) {
      console.error('クレジット購入エラー:', err)
      setError(err.message || 'クレジット購入に失敗しました')
    } finally {
      setCharging(false)
    }
  }

  const handleGenerate = async () => {
    try {
      setLoading(true)
      setError('')

      const request: TestGenerationRequest = {
        complianceRuleId: selectedRule!.id,
        questionCount,
        difficulty,
        userId: user?.id,
        ...(isLongContent && splitOption ? { splitOption } : {}),
      }

      console.log('AI テスト生成リクエスト送信:', request, { isLongContent, splitOption })

      // APIリクエスト送信（非同期でバックグラウンド生成が開始される）
      await TestService.generateTest(request)

      console.log('AI テスト生成リクエスト受付完了')

      // 成功コールバック（モーダルを閉じて成功メッセージを表示）
      onSuccess()
      onClose()

      // フォームリセット
      setQuestionCount(5)
      setDifficulty('medium')
      setSplitOption(null)
    } catch (err: any) {
      console.error('テスト生成エラー:', err)
      setError(err.response?.data?.error || 'テスト生成に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading && !charging) {
      setError('')
      setSelectedRule(rule || null) // リセット
      onClose()
    }
  }

  // 通貨フォーマット
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading || charging}
    >
      <DialogTitle>AI テスト生成</DialogTitle>

      <DialogContent>
        {/* 学習教材選択（rule未指定時のみ表示） */}
        {!rule && (
          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>学習教材を選択</InputLabel>
            <Select
              value={selectedRule?.id || ''}
              label="学習教材を選択"
              onChange={(e) => handleRuleSelect(e.target.value as string)}
              disabled={rulesLoading || loading || charging}
            >
              {rulesLoading ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    読み込み中...
                  </Box>
                </MenuItem>
              ) : complianceRules.length === 0 ? (
                <MenuItem disabled>学習教材がありません</MenuItem>
              ) : (
                complianceRules.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.title} ({r.category})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}

        {/* 選択済み学習教材の表示 */}
        {selectedRule && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              学習教材: {selectedRule.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="textSecondary">
                カテゴリ: {selectedRule.category}
              </Typography>
              {isLongContent && (
                <Chip label={`約${pageCount}ページ`} size="small" color="secondary" />
              )}
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* クレジット情報表示 */}
        {creditLoading ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">クレジット残高を確認中...</Typography>
            </Box>
          </Alert>
        ) : isUnlimitedCredits ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              必要クレジット: {creditsRequired} / 残高: 無制限
            </Typography>
          </Alert>
        ) : isInsufficientCredit ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              クレジットが不足しています
            </Typography>
            <Typography variant="body2">
              必要クレジット: {creditsRequired} / 残高: {Math.max(0, creditBalance ?? 0)} （不足:{' '}
              {creditsShortage}）
            </Typography>
            {canCharge ? (
              <Box sx={{ mt: 1.5 }}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, mb: 1 }}>
                  <Typography variant="body2">
                    購入クレジット: <strong>{creditsShortage}クレジット</strong>
                  </Typography>
                  <Typography variant="body2">
                    購入金額: <strong>{formatCurrency(chargeCost)}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ※ 単価: {formatCurrency(aiCreditPrice)}/クレジット
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeToCharge}
                      onChange={(e) => setAgreeToCharge(e.target.checked)}
                      disabled={charging}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      上記金額でクレジットを購入してテストを生成する
                    </Typography>
                  }
                />
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mt: 1 }}>
                テストを生成するにはクレジットを追加購入するか、管理者にお問い合わせください。
              </Typography>
            )}
          </Alert>
        ) : creditBalance !== null ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              必要クレジット: {creditsRequired} / 残高: {Math.max(0, creditBalance)}
            </Typography>
          </Alert>
        ) : null}

        {/* 長いコンテンツの場合は分割オプションを表示 */}
        {isLongContent && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              この学習教材は長い文書です（約{pageCount}ページ）。分割方法を選択してください。
            </Typography>
            <FormControl component="fieldset" disabled={loading}>
              <RadioGroup
                value={splitOption || ''}
                onChange={(e) => setSplitOption(e.target.value as 'multiple' | 'combined')}
              >
                <FormControlLabel
                  value="multiple"
                  control={<Radio />}
                  label="複数のテストに分割（各セクション×指定問題数のテストを作成）"
                />
                <FormControlLabel
                  value="combined"
                  control={<Radio />}
                  label="1つのテストに結合（指定問題数×セクション数の問題を1テストに）"
                />
              </RadioGroup>
            </FormControl>
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label={isLongContent && splitOption ? 'セクションあたりの問題数' : '問題数'}
            type="number"
            value={questionCount}
            onChange={(e) => setQuestionCount(parseInt(e.target.value))}
            inputProps={{ min: 1, max: maxQuestions }}
            fullWidth
            helperText={
              isLongContent && splitOption === 'multiple'
                ? `各セクションに${questionCount}問ずつ出題されます`
                : isLongContent && splitOption === 'combined'
                  ? `合計${questionCount}問 × セクション数 の問題が1つのテストに含まれます`
                  : `1〜${maxQuestions}問まで指定できます`
            }
            disabled={loading || charging}
          />

          <FormControl fullWidth disabled={loading || charging}>
            <InputLabel>難易度</InputLabel>
            <Select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
              label="難易度"
            >
              <MenuItem value="easy">初級 - 基本的な理解を確認</MenuItem>
              <MenuItem value="medium">中級 - 実践的な応用を問う</MenuItem>
              <MenuItem value="hard">上級 - 深い理解と判断力を要求</MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info">
            {splitOption === 'multiple'
              ? `セクションごとに${questionCount}問のテストが生成されます。完了後、テスト一覧に複数のテストが表示されます。`
              : splitOption === 'combined'
                ? `全セクションから合計で問題が生成され、1つのテストにまとめられます。`
                : 'AIが学習教材の内容を分析し、適切な問題を自動生成します。生成はバックグラウンドで行われ、完了後にテスト一覧に表示されます。'}
          </Alert>

          <Typography variant="caption" color="text.secondary">
            ※ 学習教材の内容が短い場合、指定した問題数より少ない問題が生成されることがあります。
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading || charging}>
          キャンセル
        </Button>
        <Button
          onClick={agreeToCharge ? handleChargeAndGenerate : handleGenerate}
          variant="contained"
          disabled={
            loading ||
            charging ||
            creditLoading ||
            !selectedRule ||
            (isInsufficientCredit && !(agreeToCharge && canCharge)) ||
            questionCount < 1 ||
            questionCount > maxQuestions ||
            (isLongContent && !splitOption)
          }
          startIcon={loading || charging ? <CircularProgress size={20} /> : null}
        >
          {loading || charging
            ? '生成中...'
            : agreeToCharge
              ? '購入してテスト生成'
              : isInsufficientCredit
                ? 'クレジット不足'
                : 'テスト生成'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
